import React from 'react';
import ReactDOM from 'react-dom/client';
import { BrowserRouter } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import { ConfigProvider, App as AntdApp, theme as antdAlgo } from 'antd';
import koKR from 'antd/locale/ko_KR';
import { useThemeStore } from './store/theme.store';
import { NavermapsProvider } from 'react-naver-maps';
import dayjs from 'dayjs';
import 'dayjs/locale/ko';
import relativeTime from 'dayjs/plugin/relativeTime';

import { App } from './App';
import './index.css';

// dayjs 한국어 설정
dayjs.locale('ko');
dayjs.extend(relativeTime);

// TanStack Query 클라이언트 설정
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 1,
      refetchOnWindowFocus: false,
      staleTime: 30_000,
    },
    mutations: {
      retry: 0,
    },
  },
});

// Ant Design 테마 — mode에 따라 algorithm 동적 분기
// 사이드바는 라이트 모드에서도 항상 다크 톤(siderBg) 유지
function ThemedApp() {
  const mode = useThemeStore((s) => s.mode);
  const isDark = mode === 'dark';
  const antdTheme = {
    algorithm: isDark ? antdAlgo.darkAlgorithm : antdAlgo.defaultAlgorithm,
    token: {
      colorPrimary: '#1890ff',
      borderRadius: 8,
      fontFamily:
        '-apple-system, BlinkMacSystemFont, "Segoe UI", "PingFang SC", "Hiragino Sans GB", "Microsoft YaHei", sans-serif',
    },
    components: {
      Layout: {
        siderBg: '#001529',
      },
      Menu: {
        darkItemBg: '#001529',
        darkSubMenuItemBg: '#000c17',
      },
    },
  };
  return (
    <ConfigProvider locale={koKR} theme={antdTheme}>
      <NavermapsProvider ncpKeyId={import.meta.env.VITE_NAVER_MAP_KEY_ID ?? import.meta.env.VITE_NAVER_MAP_CLIENT_ID}>
        <AntdApp>
          <App />
        </AntdApp>
      </NavermapsProvider>
    </ConfigProvider>
  );
}

ReactDOM.createRoot(document.getElementById('root')!).render(
  <React.StrictMode>
    <BrowserRouter>
      <QueryClientProvider client={queryClient}>
        <ThemedApp />
        {import.meta.env.DEV && <ReactQueryDevtools initialIsOpen={false} />}
      </QueryClientProvider>
    </BrowserRouter>
  </React.StrictMode>
);
